/**
 * The only place that knows URLs. Everything else takes typed objects.
 *
 * Every call takes an AbortSignal: a reader that flips pages quickly will start
 * and abandon a lot of requests, and an abandoned request must not overwrite
 * fresher state when it finally lands.
 */

import type {
  AcquireJob,
  AcquireQueue,
  BookDetail,
  Chapter,
  DiscoverResult,
  Facets,
  LibraryQuery,
  LibraryResponse,
  PageMap,
  Progress,
  SearchHit,
  SyncPayload,
} from "./types";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Fired when the server refuses a call for want of a session, which happens
 * when a cookie expires under a reader who is still on the page. Announced
 * once, here, rather than handled in each of the twenty call sites: they would
 * all have to agree, and one that forgot would leave the screen frozen on a
 * silent error.
 */
export const UNAUTHORIZED = "readcommons:unauthorized";

function raise(status: number, path: string, detail: string): never {
  if (status === 401) window.dispatchEvent(new Event(UNAUTHORIZED));
  throw new ApiError(status, detail || `${path} answered ${status}`);
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal });
  if (!response.ok) {
    raise(response.status, path, await response.text().catch(() => ""));
  }
  return (await response.json()) as T;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/** What the server says about the door. */
export type Session = { required: boolean; user: string | null };

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail = "";
    try {
      detail = ((await response.json()) as { detail?: string }).detail ?? "";
    } catch {
      detail = "";
    }
    raise(response.status, path, detail);
  }
  return (await response.json()) as T;
}

export const api = {
  session: (signal?: AbortSignal) => get<Session>("/api/auth/me", signal),

  login: (user: string, password: string) =>
    post<Session>("/api/auth/login", { user, password }),

  logout: () => post<Session>("/api/auth/logout", {}),

  health: (signal?: AbortSignal) =>
    get<{ status: string; version: string; api: number }>("/api/health", signal),

  library: (q: LibraryQuery = {}, signal?: AbortSignal) =>
    get<LibraryResponse>(`/api/library${query({ ...q })}`, signal),

  // `prefer` is passed on so the status counts are counted over works rather
  // than editions, which is what the grid beside them shows.
  facets: (prefer?: string, signal?: AbortSignal) =>
    get<Facets>(`/api/facets${query({ prefer })}`, signal),

  book: (id: string, signal?: AbortSignal) =>
    get<BookDetail>(`/api/books/${encodeURIComponent(id)}`, signal),

  chapter: (id: string, index: number, signal?: AbortSignal) =>
    get<Chapter>(`/api/books/${encodeURIComponent(id)}/chapters/${index}`, signal),

  sync: (id: string, signal?: AbortSignal) =>
    get<SyncPayload>(`/api/books/${encodeURIComponent(id)}/sync`, signal),

  pagemap: (id: string, signal?: AbortSignal) =>
    get<PageMap>(`/api/books/${encodeURIComponent(id)}/pagemap`, signal),

  search: (q: string, options: { book?: string; limit?: number } = {}, signal?: AbortSignal) =>
    get<{ hits: SearchHit[] }>(`/api/search${query({ q, ...options })}`, signal),

  // Books that are *not* here yet. Separate from `search` on purpose: that one
  // reads a local index in a millisecond, this one asks three catalogues over
  // the network and can take seconds. One call for both would make the fast
  // answer wait for the slow one.
  discover: (q: string, limit = 8, signal?: AbortSignal) =>
    get<{ results: DiscoverResult[] }>(`/api/discover${query({ q, limit })}`, signal),

  queue: (signal?: AbortSignal) => get<AcquireQueue>("/api/acquire", signal),

  acquire: (find: DiscoverResult) =>
    post<AcquireJob>("/api/acquire", {
      source: find.source,
      ident: find.ident,
      title: find.title,
      author: find.author,
      year: find.year,
      lang: find.lang,
      cover_url: find.cover_url,
    }),

  async unqueue(id: number): Promise<AcquireQueue> {
    const response = await fetch(`/api/acquire/${id}`, { method: "DELETE" });
    if (!response.ok) raise(response.status, "/api/acquire", "");
    return (await response.json()) as AcquireQueue;
  },

  progress: (id: string, signal?: AbortSignal) =>
    get<Progress | null>(`/api/books/${encodeURIComponent(id)}/progress`, signal),

  async saveProgress(id: string, sentence: number, seconds: number): Promise<void> {
    // keepalive so the last position still lands when the tab is closing.
    await fetch(`/api/books/${encodeURIComponent(id)}/progress`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sentence, seconds }),
      keepalive: true,
    });
  },

  // The document and the audio are the two largest things this app fetches, by
  // a factor of a thousand: a hundred megabytes of scan, a gigabyte and a half
  // of narration. The revision goes in the query string so the server can mark
  // them immutable and the browser never asks for either of them twice. Drop
  // the revision and you drop the whole caching story with it.
  audioUrl: (id: string, rev?: string | null) =>
    `/api/books/${encodeURIComponent(id)}/audio${rev ? `?v=${rev}` : ""}`,
  docUrl: (id: string, rev?: string | null) =>
    `/api/books/${encodeURIComponent(id)}/doc${rev ? `?v=${rev}` : ""}`,
};
